import CRMStatusChip from '@components/Common/CRMStatusChip';
import CRMPageTemplate from '@components/Common/CRMPageTemplate';
import ErrorMessage from '@components/Common/ErrorMessage';
import type { Column } from '@components/Common/SearchFilter/SearchFilter';
import SkeletonLoader from '@components/Common/Skeleton/SkeletonLoader';
import MintlyTable from '@components/Common/Table';
import { isSuperAdmin, useCurrentUserRole } from '@components/Common/Utils/roles';
import EmployerCreateDialog from './EmployerCreateDialog';
import { employers } from '@constants/graphQLQueries';
import { fetchGraphQLApiData } from '@helpers/graphQLApi';
import { buildFilterArray, graphQLFilterArrayFormatter } from '@helpers/utils';
import PersonSearchIcon from '@mui/icons-material/PersonSearch';
import { Box, Typography } from '@mui/material';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { formatDate, formatDateTime, formatRelative } from '@helpers/date';

/* The table's columns, named once so the loading skeleton can draw the same number of
   them. A skeleton with its own column count is a skeleton that stops matching. */
const EMPLOYER_COLUMNS = ['name', 'status', 'email', 'location', 'lastLogin', 'createdAt'];

const rowsPerPage = 100;
interface EmployerData {
  id: string;
  name: string;
  email: string;
  phone: string;
  city: string;
  state: string;
  country: string;
  lastLoginAt: string | null;
  createdAt?: string | null;
  isPremium?: boolean;
  premiumRequestStatus?: string | null;
}

// Last login is relative for a week and a date after that; see formatRelative.
const formatLastLogin = (value: string | null | undefined): string => formatRelative(value);

const Employers: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  // Mirrors the BFF: POST /api/employers is mounted superAdminOnly, so offering an HR the button
  // would only ever produce a 403.
  const canCreate = isSuperAdmin(useCurrentUserRole());
  const [page, setPage] = useState(() => {
    const p = parseInt(searchParams.get('page') ?? '0', 10);
    return Number.isFinite(p) && p >= 0 ? p : 0;
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [data, setData] = useState<EmployerData[]>([]);
  const [totalElements, setTotalElements] = useState(0);
  const [filterData, setFilterData] = useState<Column[]>(() => {
    const initial: Column[] = [];
    searchParams.forEach((value, key) => {
      if (key.startsWith('filter_') && value.trim() !== '') {
        const col = key.replace('filter_', '');
        initial.push({ id: initial.length + 1, columnName: col, searchValue: decodeURIComponent(value) });
      }
    });
    return initial;
  });
  const [dateFrom, setDateFrom] = useState(() => searchParams.get('date_from') ?? '');
  const [dateTo, setDateTo] = useState(() => searchParams.get('date_to') ?? '');

  const navigate = useNavigate();

  /* The three chips were MUI semantic colours: a different green from --color-status-success and
     a different amber from --color-status-warning, so an employer's Managed chip did not match the
     Active chip on the company beside it. The word is still ours; the colour is not. */
  const renderStatus = (row: any) => {
    if (row.isPremium) {
      return <CRMStatusChip status="active" label="Premium Hiring" />;
    }
    if (row.premiumRequestStatus === 'PENDING') {
      return <CRMStatusChip status="pending" label="Premium Hiring requested" />;
    }
    if (row.premiumRequestStatus === 'CONTRACT_SENT') {
      return <CRMStatusChip status="open" label="Contract sent" />;
    }
    return (
      <Typography variant="body2" color="text.secondary">
        Regular
      </Typography>
    );
  };

  const renderLocation = (row: any) => {
    const { city, state, country } = row;
    if (city && state && country) return `${city}, ${state}, ${country}`;
    if (city && state) return `${city}, ${state}`;
    if (state && country) return `${state}, ${country}`;
    if (city && country) return `${city}, ${country}`;
    if (state) return state;
    if (country) return country;
    return '-';
  };

  const userColMapping: Record<string, string | string[]> = {
    Name: 'personalDetails.fullName',
    Email: 'personalDetails.email',
    'Phone Number': 'personalDetails.phone',
    City: 'personalDetails.city',
    State: 'personalDetails.state',
    Country: 'personalDetails.country',
    'Premium Hiring Request': 'premiumRequestStatus',
    'Account Review': 'approvalStatus',
    /*
     * Regular vs Managed, off the same flag the rest of the system reads.
     *
     * Only 7 of 2,210 employers carry isPremium at all -- the other 2,203 have no such field --
     * so "Regular" only works because FilterCriteriaUtils treats a boolean `false` as
     * false-or-absent. Filtering on `is(false)` alone would have returned zero rows and looked
     * exactly like the broken filters this change is about.
     */
    Status: 'isPremium',
  };

  // Predefined values for the "Managed Hiring Request" filter (rendered as selectable chips in the
  // filter sidebar). Values must match what is stored on the employer document.
  const columnOptions: Record<string, string[]> = {
    'Premium Hiring Request': ['PENDING', 'CONTRACT_SENT', 'REJECTED'],
    // Filtering by PENDING is the review queue: there is no separate screen, because the list
    // already has the search, paging and filters a queue needs.
    'Account Review': ['PENDING', 'APPROVED', 'DECLINED'],
    // Values are what the filter sends, not what it shows: isPremium is a boolean, and
    // FilterCriteriaUtils keys off the literal strings "true"/"false".
    Status: ['true', 'false'],
  };

  /*
   * Status is the one column whose stored value is not a word anybody would recognise. The chip
   * label comes from here; everything else is humanised from the value itself.
   */
  const optionLabels: Record<string, Record<string, string>> = {
    Status: { true: 'Premium Hiring', false: 'Regular' },
  };

  const filterColumns = Object.keys(userColMapping);
  const skipResetOnFirstFilterChange = useRef(true);

  const onApplyFilters = useCallback(() => {
    if (skipResetOnFirstFilterChange.current) {
      skipResetOnFirstFilterChange.current = false;
      return;
    }
    if (page !== 0) setPage(0);
  }, [page]);

  const handleChangePage = useCallback(
    (_event: React.MouseEvent<HTMLButtonElement> | null, newPage: number) => {
      setPage(newPage);
      const newSearch = new URLSearchParams(searchParams);
      newSearch.set('page', String(newPage));
      if (newSearch.toString() !== searchParams.toString()) setSearchParams(newSearch);
    },
    [searchParams, setSearchParams],
  );

  const fetchData = useCallback(
    async (page: number) => {
      setLoading(true);
      setError(null);
      const filterArr = buildFilterArray(filterData, userColMapping);
      if (dateFrom) filterArr.push({ key: 'createdAtFrom', value: dateFrom, operator: 'AND' });
      if (dateTo) filterArr.push({ key: 'createdAtTo', value: dateTo, operator: 'AND' });
      const filter = graphQLFilterArrayFormatter(filterArr);
      try {
        const query = employers(page, rowsPerPage, filter);
        const { data } = await fetchGraphQLApiData(query);
        const { getAllEmployers } = data;
        // Shape, not truthiness: totalElements of 0 is falsy, so a filter matching nothing would
        // otherwise surface as a fetch error rather than an empty list.
        if (getAllEmployers && Array.isArray(getAllEmployers.content)) {
          const employersList: EmployerData[] = getAllEmployers.content.map((item: any) => ({
            id: item._id,
            name: (() => {
              const pd = item.personalDetails || {};
              const full = (pd.fullName || '').toString().trim();
              if (full) return full;
              const first = (pd.firstName || '').toString().trim();
              const last = (pd.lastName || '').toString().trim();
              return [first, last].filter(Boolean).join(' ').trim() || '-';
            })(),
            email: item.personalDetails.email,
            phone: item.personalDetails.phone || '',
            city: item.personalDetails.city,
            state: item.personalDetails.state,
            country: item.personalDetails.country,
            lastLoginAt: item.lastLoginAt ?? null,
            createdAt: item.createdAt ?? null,
            isPremium: item.isPremium ?? false,
            premiumRequestStatus: item.premiumRequestStatus ?? null,
          }));
          setData(employersList);
          setTotalElements(getAllEmployers.totalElements ?? 0);
        } else {
          throw new Error('Unexpected response format');
        }
      } catch (error) {
        console.error('Error fetching employers:', error);
        setError('Failed to fetch data. Please try again later.');
      } finally {
        setLoading(false);
      }
    },
    [filterData, dateFrom, dateTo],
  );

  useEffect(() => { fetchData(page); }, [fetchData, page, filterData]);

  useEffect(() => {
    const p = parseInt(searchParams.get('page') ?? '0', 10);
    const safe = Number.isFinite(p) && p >= 0 ? p : 0;
    if (safe !== page) setPage(safe);
    setDateFrom(searchParams.get('date_from') ?? '');
    setDateTo(searchParams.get('date_to') ?? '');
  }, [searchParams.toString()]);

  const skipDateUrlSyncOnMount = useRef(true);
  useEffect(() => {
    if (skipDateUrlSyncOnMount.current) {
      skipDateUrlSyncOnMount.current = false;
      return;
    }
    setSearchParams(prev => {
      const n = new URLSearchParams(prev);
      if (dateFrom) n.set('date_from', dateFrom); else n.delete('date_from');
      if (dateTo) n.set('date_to', dateTo); else n.delete('date_to');
      return n;
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateFrom, dateTo]);

  // A row opens the entity workspace at its own route. This used to swap the
  // list out for a popup rendered in place, so the URL and the screen could
  // disagree.
  const handleOpenRecord = (userId: string) => navigate(`/employers/${userId}`);

  return (
    <CRMPageTemplate
      addNewLabel="Create Employer"
      onAddNew={canCreate ? () => setCreateOpen(true) : undefined}
      filterColumns={filterColumns}
      columnOptions={columnOptions}
      optionLabels={optionLabels}
      locationColumns={['City', 'State', 'Country']}
      filterLabel="Filter"
      icon={<PersonSearchIcon />}
      initialColumns={filterData}
      setFilterData={setFilterData}
      title="Employers"
      totalCount={totalElements}
      onApplyFilters={onApplyFilters}
      dateFrom={dateFrom}
      dateTo={dateTo}
      onDateFromChange={v => setDateFrom(v)}
      onDateToChange={v => setDateTo(v)}
    >
      {loading ? (
        <SkeletonLoader rows={20} columns={EMPLOYER_COLUMNS.length} />
      ) : error ? (
        <ErrorMessage message={error} />
      ) : data.length === 0 ? (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 1, py: 8 }}>
          <Typography color="text.secondary" variant="body2">No employers found.</Typography>
        </Box>
      ) : (
        <MintlyTable
          pagination={{
            enabled: true,
            page,
            rowsPerPage,
            totalNumberOfRows: totalElements,
            handleChangePage,
          }}
          rows={data}
          selectors={EMPLOYER_COLUMNS}
          tableBodyRow={{
            name: (row: any) => <span>{row.name || '-'}</span>,
            status: (row: any) => renderStatus(row),
            email: (row: any) => <>{row.email || '-'}</>,
            location: (row: any) => <>{renderLocation(row)}</>,
            lastLogin: (row: any) => (
              <span title={row.lastLoginAt ? formatDateTime(row.lastLoginAt) : 'Never logged in'}
                style={{ color: !row.lastLoginAt ? 'var(--color-text-muted)' : 'inherit' }}>
                {formatLastLogin(row.lastLoginAt)}
              </span>
            ),
            createdAt: (row: any) => <>{formatDate(row.createdAt)}</>,
          }}
          tableHeadRow={{
            name: <>Name</>,
            status: <>Status</>,
            email: <>Email</>,
            location: <>Location</>,
            lastLogin: <>Last Login</>,
            createdAt: <>Created</>,
          }}
          onRowClick={(row) => handleOpenRecord(row.id)}
        />
      )}
      <EmployerCreateDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={() => fetchData(page)}
      />
    </CRMPageTemplate>
  );
};

export default Employers;
